import { isDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';

import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorSelector,
  useTargetAccumulatorTileId,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TargetAccumulatorFormField } from '@/App/tiles/TargetAccumulator/TargetAccumulatorFormField.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput/index.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectTargetAccumulatorHedgeData,
  selectTargetAccumulatorRfsHedgeAmount,
  selectTargetAccumulatorValidation,
} from '@/store/targetAccumulator/selectors.ts';

export function HedgeAmount() {
  const {
    currentUser: {
      featureToggles: { accumulatorAllowOverrideHedge: allowHedgeAmountOverride },
    },
  } = useUserContext();
  const tileId = useTargetAccumulatorTileId();

  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { hedgeAmount, hedgeCurrencyIndex } = useAppSelector((state) =>
    selectTargetAccumulatorHedgeData(state, tileId),
  );
  const rfsHedgeAmount = useTargetAccumulatorSelector((targetAccumulator) =>
    selectTargetAccumulatorRfsHedgeAmount(targetAccumulator),
  );
  const currencyPair = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator?.properties.currencyPair);
  const [currency1, currency2] = currencyPair ? currencyPair.split('/') : ['', ''];

  const validation = useAppSelector((state) => selectTargetAccumulatorValidation(state, tileId));

  const isPinned = isDefined(hedgeAmount);

  const handleHedgeAmountChanged = (amount: number | null | undefined) => {
    sgwtWebAnalytics?.trackEvent('TargetAccumulator', 'Hedge amount selected', amount?.toString() ?? '');

    checkTargetAccumulatorWithTradeCapture({
      hedgeAmount: amount,
      hedgeCurrencyIndex,
    });
  };

  const onCurrencyChange = () => {
    checkTargetAccumulatorWithTradeCapture({
      hedgeAmount,
      hedgeCurrencyIndex: hedgeCurrencyIndex === 1 ? 2 : 1,
    });
  };

  return (
    <TargetAccumulatorFormField formattedMessageId="targetAccumulator.form.hedgeAmount">
      {allowHedgeAmountOverride ? (
        <div className="d-flex px-1">
          <TradeCaptureValidationWrapper validation={validation?.hedgeAmount}>
            <SgAmountInput
              size="sm"
              data-e2e="HedgeNotional"
              value={hedgeAmount ?? (isDefined(rfsHedgeAmount) ? Number(rfsHedgeAmount) : undefined)}
              className={isPinned ? 'text-info fw-bold ' : undefined}
              unit={hedgeCurrencyIndex === 1 ? currency1 : currency2}
              onChange={handleHedgeAmountChanged}
              onUnitClick={onCurrencyChange}
              allowNegative={false}
            />
          </TradeCaptureValidationWrapper>
          {isPinned && (
            <button
              onClick={() => handleHedgeAmountChanged(null)}
              type="button"
              className="btn btn-sm btn-icon btn-flat-primary"
              data-e2e="spotRef-pin"
            >
              <em className="icon icon-sm cursor-default line-height-1 text-secondary">push_pin</em>
            </button>
          )}
        </div>
      ) : (
        <SgAmountInput
          size="sm"
          data-e2e="HedgeNotional"
          value={isDefined(rfsHedgeAmount) ? Number(rfsHedgeAmount) : undefined}
          unit={hedgeCurrencyIndex === 1 ? currency1 : currency2}
          disabled
        />
      )}
    </TargetAccumulatorFormField>
  );
}
